<template>
  <section
    v-if="!loading && events.length"
    class="hl-carousel"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="hl-track" :style="{ transform: `translateX(-${activeIndex * 100}%)` }">
      <div
        v-for="event in events"
        :key="(event.link || '') + event.date"
        class="hl-slide"
        @click="$emit('select', event)"
      >
        <div class="hl-image-wrap">
          <img
            :src="event.imageUrl"
            :alt="event.title"
            class="hl-image"
            loading="lazy"
          />
          <div class="hl-overlay"></div>
        </div>
        <div class="hl-content">
          <span class="hl-eyebrow">Highlighted</span>
          <h2 class="hl-title">{{ event.title }}</h2>
          <p class="hl-meta">
            {{ formatDate(event.date) }} · {{ (event.venue || "").toUpperCase() }}
            <span v-if="shouldShowGenre(event)" class="hl-genre">{{ event.genre }}</span>
          </p>
        </div>
      </div>
    </div>

    <button
      v-if="events.length > 1"
      class="hl-arrow hl-arrow--prev"
      @click.stop="prev"
      aria-label="Föregående"
    >
      ‹
    </button>
    <button
      v-if="events.length > 1"
      class="hl-arrow hl-arrow--next"
      @click.stop="next"
      aria-label="Nästa"
    >
      ›
    </button>

    <div v-if="events.length > 1" class="hl-dots">
      <button
        v-for="(_, i) in events"
        :key="i"
        :class="['hl-dot', { 'hl-dot--active': i === activeIndex }]"
        @click.stop="goTo(i)"
        :aria-label="`Gå till bild ${i + 1}`"
      ></button>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { shouldShowGenre } from "@/utils/genreDisplay";

export default {
  name: "HighlightedCarousel",
  props: {
    city: {
      type: String,
      default: "Göteborg",
    },
    autoAdvanceMs: {
      type: Number,
      default: 6000,
    },
  },
  emits: ["select"],
  data() {
    return {
      events: [],
      loading: true,
      activeIndex: 0,
      timer: null,
      paused: false,
    };
  },
  methods: {
    // Genre visas bara för inloggade + tillräcklig AI-confidence (eller admin)
    shouldShowGenre,
    async fetchEvents() {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL +
            "events/highlighted/" +
            encodeURIComponent(this.city)
        );
        this.events = data || [];
      } catch (err) {
        console.error("Failed to fetch highlighted events:", err.message || err);
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return "";
      try {
        return new Date(d).toLocaleDateString("sv-SE", {
          weekday: "short",
          day: "numeric",
          month: "long",
        });
      } catch (e) {
        return d;
      }
    },
    next() {
      this.activeIndex = (this.activeIndex + 1) % this.events.length;
    },
    prev() {
      this.activeIndex =
        (this.activeIndex - 1 + this.events.length) % this.events.length;
    },
    goTo(i) {
      this.activeIndex = i;
    },
    startAutoAdvance() {
      this.stopAutoAdvance();
      if (this.events.length <= 1 || this.autoAdvanceMs <= 0) return;
      this.timer = setInterval(() => {
        if (!this.paused) this.next();
      }, this.autoAdvanceMs);
    },
    stopAutoAdvance() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    },
  },
  watch: {
    events() {
      this.activeIndex = 0;
      this.startAutoAdvance();
    },
  },
  async created() {
    await this.fetchEvents();
  },
  beforeUnmount() {
    this.stopAutoAdvance();
  },
};
</script>

<style scoped>
.hl-carousel {
  position: relative;
  width: 100%;
  margin-bottom: 3rem;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16 / 6;
  max-height: 420px;
  background: var(--color-bg-soft, #111);
}

.hl-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.hl-slide {
  flex: 0 0 100%;
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.hl-image-wrap {
  position: absolute;
  inset: 0;
}

.hl-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hl-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
}

.hl-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem 2.5rem;
  color: #fff;
}

.hl-eyebrow {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-accent, #d4a017);
  margin-bottom: 0.5rem;
}

.hl-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.5rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 1.05;
  margin: 0 0 0.5rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.hl-meta {
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  font-weight: 500;
  margin: 0;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.hl-genre {
  background: rgba(255, 255, 255, 0.15);
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.hl-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  z-index: 2;
}

.hl-arrow:hover {
  background: rgba(0, 0, 0, 0.7);
}

.hl-arrow--prev {
  left: 1rem;
}

.hl-arrow--next {
  right: 1rem;
}

.hl-dots {
  position: absolute;
  bottom: 1rem;
  right: 1.5rem;
  display: flex;
  gap: 0.4rem;
  z-index: 2;
}

.hl-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, transform 0.15s ease;
}

.hl-dot--active {
  background: #fff;
  transform: scale(1.25);
}

@media (max-width: 700px) {
  .hl-carousel {
    aspect-ratio: 4 / 3;
  }
  .hl-content {
    padding: 1.25rem 1.5rem;
  }
  .hl-arrow {
    width: 36px;
    height: 36px;
  }
}
</style>
